/**
 * メールソフト設定情報（05-ui.md §8.4 clients タブの独立画面版）。
 *
 * 「メールソフトに何を入れればいいのか分からない」は最も多い問い合わせなので、
 * 値をコピーできる形で出し、機種別の手順もこの 1 画面に載せる。
 * ポート番号は `lib/mail-client-settings.ts`（= ARCHITECTURE §5.2）が唯一の正。
 */
import * as React from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CopyableCode,
  Field,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@mail-sv/ui';
import { Download, Printer } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useDomains, useOrg } from '../lib/hooks.js';
import { AppLink, useAppNavigate } from '../lib/nav.js';
import { Async, LoadingBlock } from '../components/async.js';
import { Disclosure, HelpNote } from '../components/help.js';
import { buildMailClientSettings, renderSettingsText } from '../lib/mail-client-settings.js';
import { env } from '../env.js';
import type { CertificateState, CursorPage, Mailbox } from '../lib/api-types.js';

export function MailClientsPage() {
  const org = useOrg();
  const [address, setAddress] = React.useState<string>('');

  const mailboxes = useQuery({
    queryKey: qk.mailboxes({ forClients: true }),
    queryFn: () => api.get<CursorPage<Mailbox>>('/mailboxes?limit=200'),
    staleTime: 60_000,
  });

  const list = mailboxes.data?.items ?? [];
  const selected = address || list[0]?.address || '';
  // 印刷用画面はアドレスではなく id で受けるので、選んだ行から引く。
  const selectedId = list.find((m) => m.address === selected)?.id;

  return (
    <>
      <PageHeader
        title="メールソフトの設定"
        description="Outlook・Thunderbird・iPhone などにメールアドレスを設定するための情報です。"
      />

      <div className="space-y-5">
        <HelpNote title="設定でつまずいたら">
          ほとんどの失敗は「ユーザー名をメールアドレス全体にしていない」か「暗号化の種類が違う」のどちらかです。
          下の表の値をそのままコピーしてお使いください。
        </HelpNote>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">どのメールアドレスの設定を表示しますか？</CardTitle>
          </CardHeader>
          <CardContent>
            {mailboxes.isPending ? (
              <LoadingBlock lines={1} />
            ) : list.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                まだメールアドレスがありません。先にメールアドレスを作成してください。
              </p>
            ) : (
              <Field label="メールアドレス" className="max-w-md">
                <Select value={selected} onValueChange={setAddress}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {list.map((m) => (
                      <SelectItem key={m.id} value={m.address}>
                        {m.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </CardContent>
        </Card>

        <Async query={org} lines={5}>
          {(data) => (
            <>
              <MailClientTable
                host={data.node.mailHostname || env.fallbackMailHostname}
                address={selected || 'あなたのメールアドレス'}
                mailboxId={selectedId}
              />
              <CustomHostnameCard
                sharedHost={data.node.mailHostname || env.fallbackMailHostname}
                available={data.plan.features.customMailHostname}
              />
            </>
          )}
        </Async>

      </div>
    </>
  );
}

/** 証明書の状態の見せ方。domain-detail.tsx の `CERT_STATUS` と同じ訳語にそろえる。 */
const CERT_VIEW: Record<
  string,
  { label: string; tone: 'success' | 'info' | 'destructive' | 'muted' }
> = {
  active: { label: '使えます', tone: 'success' },
  renewing: { label: '使えます（更新中）', tone: 'success' },
  pending: { label: '準備中', tone: 'info' },
  issuing: { label: '準備中', tone: 'info' },
  failed: { label: '取得に失敗', tone: 'destructive' },
  revoked: { label: '失効', tone: 'destructive' },
  expired: { label: '期限切れ', tone: 'destructive' },
  not_requested: { label: 'まだ発行されていません', tone: 'muted' },
};

const CERT_BADGE = {
  success: 'success',
  info: 'info',
  destructive: 'destructive',
  muted: 'muted',
} as const;

/**
 * お客様のドメイン名で接続できるか（`GET /domains/:id/certificate`）。
 *
 * **この画面に置く理由。** 証明書の状態が見えないと、メールソフトの接続先を
 * 共通ホスト名から `mail.<自ドメイン>` へ切り替えてよいのか判断できず、
 * 機能があっても使われない（API 側のコメントもその前提で書かれている）。
 * 判断するのはまさにこの画面（接続先の値をコピーする場所）なので、
 * ドメイン詳細のタブだけに置くと届かない。
 *
 * **候補のホスト名は `mail.<ドメイン名>`。** 証明書を作る
 * `ensureDomainCertificate()`（`apps/worker/src/workers/acme.ts`）が
 * `commonName = mail.<domain.name>` で発行するので、そこに合わせる。
 * `domains.mail_hostname` は**ノード側の受信ホスト名**（既定は共通の
 * `mx1.<サービスドメイン>`）で別物なので、そちらを候補として出すと
 * 「共通ホスト名を独自ホスト名として案内する」ことになる。
 *
 * **プランに無いときは表を出さない。** `domain.certificate` のステップが
 * `features.customMailHostname` で skip されるため、証明書は永久に
 * `not_requested` のままになる。並べても「未発行」が並ぶだけで、
 * 何を待てばよいのか分からない画面になる。
 *
 * **「使えます」以外は切り替えを勧めない。** 証明書が無い状態で接続先だけ
 * 変えると、メールソフトが証明書の警告を出して送受信が止まる。
 */
function CustomHostnameCard({ sharedHost, available }: { sharedHost: string; available: boolean }) {
  const domains = useDomains();
  const list = domains.data?.items ?? [];

  const certs = useQueries({
    queries: list.map((d) => ({
      queryKey: qk.certificate(d.id),
      queryFn: () => api.get<CertificateState>(`/domains/${encodeURIComponent(d.id)}/certificate`),
      staleTime: 5 * 60_000,
      // プランに無い契約では発行されないので、そもそも問い合わせない。
      enabled: available,
      // 権限が無いメンバー（domainRead なし）では 403。出さないだけで良い。
      retry: false,
    })),
  });

  if (domains.isPending || list.length === 0) return null;

  if (!available) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">接続先のホスト名について</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            上の表の接続先（<span className="font-mono">{sharedHost}</span>）は、
            ご契約のドメインに関係なく共通でお使いいただけます。お客様のドメイン名
            （<span className="font-mono">mail.（お客様のドメイン）</span>）での接続は上位プランの機能です。
            共通の接続先でも、暗号化の強さや使える機能に違いはありません。
          </p>
        </CardContent>
      </Card>
    );
  }

  const errors = list
    .map((d, i) => ({ domain: d, error: certs[i]?.data?.lastError ?? null }))
    .filter((e): e is { domain: (typeof list)[number]; error: string } => e.error !== null);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">お客様のドメイン名で接続する</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          上の表の接続先（<span className="font-mono">{sharedHost}</span>）は共通でお使いいただけます。
          お客様のドメイン名に切り替えるには、そのホスト名の TLS
          証明書が必要です。取得と更新はこちらで自動的に行いますので、お客様の操作は要りません。
        </p>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>接続先の候補</TableHead>
              <TableHead className="w-48">状態</TableHead>
              <TableHead className="w-44">有効期限</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((d, i) => {
              const q = certs[i];
              const cert = q?.data;
              const status = cert?.status ?? 'not_requested';
              const view = CERT_VIEW[status] ?? { label: status, tone: 'muted' as const };
              return (
                <TableRow key={d.id}>
                  <TableCell className="break-all font-mono text-xs">
                    {cert?.commonName ?? `mail.${d.name}`}
                  </TableCell>
                  <TableCell>
                    {q?.isPending ? (
                      <span className="text-xs text-muted-foreground">確認しています…</span>
                    ) : q?.isError ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <Badge variant={CERT_BADGE[view.tone]} size="sm">
                        {view.label}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {cert?.notAfter
                      ? `${new Date(cert.notAfter).toLocaleDateString('ja-JP')}${
                          typeof cert.daysRemaining === 'number'
                            ? `（あと ${cert.daysRemaining} 日）`
                            : ''
                        }`
                      : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/*
          失敗理由は**そのまま出す**（DNS の TXT が未反映など、契約者自身にしか
          直せない原因がほとんど）。隠すと自力で直せる失敗までサポート起票になる。
        */}
        {errors.length > 0 ? (
          <Alert variant="warning">
            <AlertTitle>証明書を取得できていないドメインがあります</AlertTitle>
            <AlertDescription className="space-y-1">
              {errors.map((e) => (
                <p key={e.domain.id} className="text-xs">
                  <span className="font-mono">{e.domain.name}</span>: {e.error}
                </p>
              ))}
            </AlertDescription>
          </Alert>
        ) : null}

        <p className="text-sm text-muted-foreground">
          「使えます」になったドメインだけ、メールソフトの接続先を書き換えてください。
          発行が済んでいない状態で切り替えると、メールソフトが証明書の警告を出して送受信できなくなります。
        </p>

        <div className="flex flex-wrap gap-2">
          {list.map((d) => (
            <Button key={d.id} asChild variant="outline" size="sm">
              <AppLink to={`/domains/${d.id}/certificate`}>{d.name} の証明書を見る</AppLink>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * 接続情報の表 + コピー欄。メールアドレス詳細からも使う。
 *
 * `mailboxId` は印刷用画面へ渡すためだけに使う。**省略できる**のは、
 * この表を id を持たない文脈（プレビュー等）から使うことがあるため。
 * 無いときは印刷ボタンを押せなくする（押せるのに何も起きない、を作らない）。
 */
export function MailClientTable({
  host,
  address,
  mailboxId,
}: {
  host: string;
  address: string;
  mailboxId?: string;
}) {
  const navigate = useAppNavigate();
  const settings = React.useMemo(() => buildMailClientSettings(host, address), [host, address]);
  const recommended = settings.entries.filter((e) => e.recommended);
  const advanced = settings.entries.filter((e) => !e.recommended);

  const download = React.useCallback(() => {
    const text = renderSettingsText(settings, env.brandName);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mail-settings-${address.replace(/[^\w.@-]/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }, [settings, address]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">接続情報</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <CopyableCode label="メールサーバー（受信・送信 共通）" value={settings.host} />
          <CopyableCode label="ユーザー名（ログイン ID）" value={settings.username} />
        </div>

        <Alert variant="info">
          <AlertTitle>ユーザー名はメールアドレス全体です</AlertTitle>
          <AlertDescription>
            「@ より前だけ」を入力するとログインできません。パスワードはメールアドレス作成時に
            設定したものです（忘れた場合は再設定してください）。
          </AlertDescription>
        </Alert>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">用途</TableHead>
                <TableHead scope="col">種類</TableHead>
                <TableHead scope="col">サーバー名</TableHead>
                <TableHead scope="col" className="w-24 text-right">
                  ポート
                </TableHead>
                <TableHead scope="col" className="w-32">
                  暗号化
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recommended.map((e) => (
                <TableRow key={`${e.protocol}-${e.port}`}>
                  <TableCell>
                    <span className="font-medium">{e.purpose}</span>
                    <p className="text-xs text-muted-foreground">{e.note}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" size="sm">
                      {e.protocol}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.host}</TableCell>
                  <TableCell className="text-right tabular">{e.port}</TableCell>
                  <TableCell>{e.encryption}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Disclosure summary="上級者向け（別のポートを使う場合）">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">用途</TableHead>
                  <TableHead scope="col">種類</TableHead>
                  <TableHead scope="col" className="w-24 text-right">
                    ポート
                  </TableHead>
                  <TableHead scope="col" className="w-32">
                    暗号化
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {advanced.map((e) => (
                  <TableRow key={`${e.protocol}-${e.port}`}>
                    <TableCell>
                      <span className="font-medium text-foreground">{e.purpose}</span>
                      <p className="text-xs text-muted-foreground">{e.note}</p>
                    </TableCell>
                    <TableCell>{e.protocol}</TableCell>
                    <TableCell className="text-right tabular">{e.port}</TableCell>
                    <TableCell>{e.encryption}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Disclosure>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={download}>
            <Download aria-hidden />
            設定情報を保存（テキスト）
          </Button>
          {/*
            **この画面から直接 `window.print()` を呼ばない。**

            ここはアプリのレイアウトの中なので、そのまま刷るとサイドナビ・
            ページ見出し・ヘルプの吹き出しまで**ページ全体が紙に出る**。
            印刷用の画面は `/print/mail-setup` に既にあり、レイアウトの外に
            置いてある（「紙のことを知っているのはその画面だけ」という設計。
            理由は `routes/print-setup-sheet.tsx` の docstring）。
            ここで `@media print` を書き始めると、その原則が崩れて
            1 か所直すたびに他の画面の印刷が壊れる。
          */}
          <Button
            variant="outline"
            disabled={!mailboxId}
            title={mailboxId ? undefined : "印刷するメールアドレスを選んでください"}
            onClick={() =>
              navigate({ to: `/print/mail-setup?ids=${encodeURIComponent(mailboxId ?? "")}` })
            }
          >
            <Printer aria-hidden />
            印刷する
          </Button>
        </div>

        {/* 手順は値のすぐ近くに置く。理由は DeviceGuides の docstring。 */}
        <DeviceGuides />
      </CardContent>
    </Card>
  );
}

/**
 * 機種別の手順。文言は実際の画面遷移に合わせてある。
 *
 * **手順は値のすぐ近くに置く。** 以前はページ末尾の独立したカードに
 * 4 機種ぶんのアコーディオンを並べていた。手順の各行は「上の表の IMAP の
 * サーバー名」のように**表を参照している**のに、その表は画面外へ
 * スクロールアウトしている。読み手は表と手順を行き来することになり、
 * いちばん多い失敗（ユーザー名を @ より前だけにする）がまさにそこで起きる。
 *
 * 機種を 1 つ選ぶ形にして、接続情報の表の直下に出す。全機種を同時に
 * 開けなくなるが、利用者が読むのは自分の機種 1 つだけなので損は無い。
 */
interface DeviceGuide {
  key: string;
  label: string;
  steps: readonly string[];
}

const DEVICE_GUIDES: readonly DeviceGuide[] = [
  {
    key: 'outlook',
    label: 'Outlook（Windows）',
    steps: [
      'Outlook を起動し、「ファイル」→「アカウントの追加」を選びます。',
      'メールアドレスを入力し、「詳細オプション」→「自分で自分のアカウントを手動で設定」にチェックを入れて「接続」。',
      'アカウントの種類で「IMAP」を選びます。',
      '受信メール: 上の表の IMAP のサーバー名・ポート 993・暗号化 SSL/TLS。',
      '送信メール: 上の表の SMTP のサーバー名・ポート 465・暗号化 SSL/TLS。',
      'パスワードを入力して完了します。',
    ],
  },
  {
    key: 'thunderbird',
    label: 'Thunderbird',
    steps: [
      '「アカウント設定」→「アカウント操作」→「メールアカウントを追加」を開きます。',
      '名前・メールアドレス・パスワードを入力し「手動設定」を押します。',
      '受信サーバー: IMAP / ポート 993 / SSL・TLS / 認証方式「通常のパスワード認証」。',
      '送信サーバー: SMTP / ポート 465 / SSL・TLS / 認証方式「通常のパスワード認証」。',
      'ユーザー名は受信・送信ともにメールアドレス全体を入力します。',
    ],
  },
  {
    key: 'ios',
    label: 'iPhone / iPad',
    steps: [
      '「設定」→「アプリ」→「メール」→「メールアカウント」→「アカウントを追加」→「その他」。',
      '「メールアカウントを追加」を選び、名前・メールアドレス・パスワードを入力します。',
      '上部の「IMAP」を選択します。',
      '受信メールサーバー・送信メールサーバーの両方に、上の表のサーバー名を入力します。',
      'ユーザ名は両方ともメールアドレス全体、パスワードも同じものを入力します。',
    ],
  },
  {
    key: 'android',
    label: 'Android（Gmail アプリ）',
    steps: [
      'Gmail アプリのメニューから「設定」→「アカウントを追加」→「その他」。',
      'メールアドレスを入力し「手動設定」→「個人用（IMAP）」を選びます。',
      '受信サーバー設定: ユーザー名にメールアドレス全体、サーバーは上の表の値、ポート 993 / SSL/TLS。',
      '送信サーバー設定: 「ログインが必要」をオンにし、ポート 465 / SSL/TLS。',
    ],
  },
];

function DeviceGuides() {
  const [key, setKey] = React.useState(DEVICE_GUIDES[0]?.key ?? '');
  const guide = DEVICE_GUIDES.find((g) => g.key === key) ?? DEVICE_GUIDES[0];
  if (!guide) return null;

  return (
    <div className="rounded-lg border border-border bg-panel p-4">
      <p className="mb-3 text-sm font-medium">お使いのソフト・端末ごとの手順</p>

      {/*
        タブではなくボタン列にしてある。機種名は横幅を食うので、
        狭い画面では折り返させたい（タブは折り返すと選択位置を見失う）。
      */}
      <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="ソフト・端末">
        {DEVICE_GUIDES.map((g) => (
          <Button
            key={g.key}
            type="button"
            role="tab"
            aria-selected={g.key === guide.key}
            size="sm"
            variant={g.key === guide.key ? 'default' : 'outline'}
            onClick={() => setKey(g.key)}
          >
            {g.label}
          </Button>
        ))}
      </div>

      <ol className="list-decimal space-y-1 pl-5 text-sm">
        {guide.steps.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>
    </div>
  );
}
